import { isValid, parseISO } from "date-fns";

/**
 * Shared field parsing for the four cost clients.
 *
 * The cost endpoints return every value as a JSON string (the same convention `ClientDevice.php`
 * uses for `odometer` and `max_speed`), and several of them are genuinely nullable — an odometer
 * that was never captured, a due date on a mileage-only rule. The existing `parseNumber` helpers
 * in `lib/api/vehicles.ts` and `lib/api/pois.ts` collapse both the missing and the unparseable
 * case to `0`, which is right for a coordinate but wrong for money and mileage: a cost of `0 KM`
 * and a cost whose amount failed to parse must not read the same.
 */

/** A required numeric field. Unparseable or missing counts as `0`. */
export function costNumber(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(typeof value === "string" ? value.trim() : value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** A nullable numeric field. Keeps "not recorded" distinct from "recorded as zero". */
export function costNullableNumber(
  value: string | number | null | undefined,
): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(typeof value === "string" ? value.trim() : value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Upstream booleans travel as `"1"` / `"0"` strings. */
export function costBoolean(
  value: string | number | boolean | null | undefined,
  fallback = false,
): boolean {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "boolean") return value;
  return String(value).trim() === "1";
}

/** The inverse, for write payloads. */
export function toRemoteFlag(value: boolean): "1" | "0" {
  return value ? "1" : "0";
}

export function costText(value: string | null | undefined): string {
  return value?.trim() ?? "";
}

/** A nullable string field — `null` and `""` both mean "not set". */
export function costNullableText(value: string | null | undefined): string | null {
  const text = value?.trim() ?? "";
  return text === "" ? null : text;
}

/**
 * Money is sent as a decimal string so it survives the trip without picking up float noise.
 * Two decimals always, because an invoice reads `145.50`, never `145.5`.
 */
export function toRemoteAmount(value: number): string {
  return Number.isFinite(value) ? value.toFixed(2) : "0.00";
}

/**
 * A date string that is safe to hand to the formatters, or `""`.
 *
 * MySQL-backed endpoints hand back `"0000-00-00"` for an unset DATE column, and `parseISO` turns
 * that into an Invalid Date that `format()` throws a `RangeError` on. Normalising here means every
 * `date ? formatDateShort(date) : NO_VALUE` guard downstream is actually correct — a truthiness
 * check alone would let the zero-date straight through and crash the table rendering it.
 *
 * Accepts both `YYYY-MM-DD` and the `YYYY-MM-DD HH:MM:SS` the audit log timestamps use.
 */
export function costDate(value: string | null | undefined): string {
  const text = value?.trim() ?? "";
  if (text === "") return "";
  return isValid(parseISO(text)) ? text : "";
}

/** Membership test that narrows to the union, for the small enums upstream sends as free text. */
export function oneOf<T extends string>(
  value: string | null | undefined,
  allowed: readonly T[],
  fallback: T,
): T {
  const text = value?.trim() ?? "";
  return (allowed as readonly string[]).includes(text) ? (text as T) : fallback;
}
